import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../component/modal/Modal';
import InputField from '../../../component/form/FormInput';
import Button from '../../../component/ui/Button';
import { toast } from 'react-toastify';
import searchIcon from '../../../assets/image/search-status.svg';
import { FiBox, FiImage, FiTrash2 } from 'react-icons/fi';
import { createProductList } from '../../../store/action/inventory';
import { product_list } from '../../../store/action/product';

const CreateNewListModal = ({ isOpen, onClose, onSaveSuccess }) => {
	const [listName, setListName] = useState('');
	const [description, setDescription] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [availableProducts, setAvailableProducts] = useState([]);
	const [page, setPage] = useState(1);
	const [pageSize] = useState(2000);
	const [hasMore, setHasMore] = useState(true);
	const [loadingProducts, setLoadingProducts] = useState(false);
	const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
	const [errorName, setErrorName] = useState('');
	const [errorDesc, setErrorDesc] = useState('');
	const [loading, setLoading] = useState(false);
	const [isSearchFocused, setIsSearchFocused] = useState(false);

	const fetchProducts = useCallback(
		async (pageNum, isReset = false) => {
			if (!isOpen) return;
			isReset ? setLoadingProducts(true) : setLoadingMoreProducts(true);

			try {
				const paramStr = `page=${pageNum}&pageSize=${pageSize}&filterBy=status&filterValue=1`;

				const res = await product_list(paramStr);
				const items =
					res?.data?.productWithStockCount ||
					res?.productWithStockCount ||
					res?.data?.products ||
					res?.products ||
					[];

				const mapped = (items || []).map((p) => ({
					id: p.product_id || p.id,
					name: p.product_name || p.name || '',
					code: p.product_code || p.code || p.UPC || p.upc || String(p.product_id || p.id || '-'),
					image: p.image || null,
					category: p.category_name || p.org_packaging?.name || p.category || p.CATEGORY || 'General',
					raw: p,
				}));

				const totalPages = res?.data?.totalPages || res?.totalPages;
				setHasMore(totalPages ? pageNum < Number(totalPages) : items.length === pageSize);

				setAvailableProducts((prev) => {
					if (isReset) return mapped;
					const existingIds = new Set(prev.map((item) => String(item.id)));
					return [...prev, ...mapped.filter((item) => !existingIds.has(String(item.id)))];
				});
			} catch (err) {
				console.error('Failed to load products:', err);
			} finally {
				setLoadingProducts(false);
				setLoadingMoreProducts(false);
			}
		},
		[isOpen, pageSize]
	);

	useEffect(() => {
		if (isOpen) {
			setPage(1);
			setHasMore(true);
			fetchProducts(1, true);
		}
	}, [isOpen, fetchProducts]);

	const handleDropdownScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (scrollHeight - scrollTop - clientHeight <= 80) {
			if (!loadingProducts && !loadingMoreProducts && hasMore) {
				const nextPage = page + 1;
				setPage(nextPage);
				fetchProducts(nextPage, false);
			}
		}
	};

	const filteredProducts = availableProducts.filter((item) => {
		if (!searchQuery.trim()) return true;
		const query = searchQuery.toLowerCase().trim();
		const nameMatch = item.name ? item.name.toLowerCase().includes(query) : false;
		const codeMatch = item.code ? String(item.code).toLowerCase().includes(query) : false;
		const categoryMatch = item.category ? item.category.toLowerCase().includes(query) : false;
		const idMatch = item.id ? String(item.id).toLowerCase().includes(query) : false;
		return nameMatch || codeMatch || categoryMatch || idMatch;
	});

	// Toggle product selection in list
	const toggleProduct = (product) => {
		setSelectedProducts((prev) =>
			prev.some((p) => (p.id || p.product_id) === (product.id || product.product_id))
				? prev.filter((p) => (p.id || p.product_id) !== (product.id || product.product_id))
				: [...prev, product]
		);
	};

	// Reset form and close modal
	const handleClose = () => {
		setListName('');
		setDescription('');
		setSearchQuery('');
		setSelectedProducts([]);
		setErrorName('');
		setErrorDesc('');
		setIsSearchFocused(false);
		onClose();
	};

	// Handle form submission and create new list via API
	const handleSubmit = async (e) => {
		if (e) e.preventDefault();

		if (!listName.trim()) {
			setErrorName('List Name is required');
			return;
		}
		if (!description.trim()) {
			setErrorDesc('Description is required');
			return;
		}

		try {
			setLoading(true);
			const payload = {
				name: listName.trim(),
				description: description.trim(),
				status: 1,
				product_ids: selectedProducts.map((p) => p.id || p.product_id),
			};

			const res = await createProductList(payload);
			if (res?.success) {
				toast.success(res?.message || 'Product List created successfully');
				if (onSaveSuccess) await onSaveSuccess();
				handleClose();
			} else {
				const isDuplicate = res?.response?.status === 409 || res?.status === 409;
				toast.error(isDuplicate ? 'Product list name already exist' : (res?.response?.data?.message || res?.message || 'Failed to create list'));
			}
		} catch (err) {
			const isDuplicate = err?.response?.status === 409;
			toast.error(isDuplicate ? 'Product list name already exist' : (err?.response?.data?.message || err?.message || 'Failed to create list'));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div data-testid="product-list-create-modal">
			<Modal
				className="add-productgroup-modal create-list-modal-wrapper"
				headerClassName="bg-white border-0"
				isOpen={isOpen}
				onClose={handleClose}
				cancelButton={handleClose}
				title="Create New List"
				dataTestId="product-list-create-modal"
			>
				<form onSubmit={handleSubmit} noValidate>
					{/* List Name Input */}
					<div className="mb-3">
						<InputField
							label="List Name"
							placeholder="Enter list name"
							type="text"
							required
							name="list_name"
							autoFocus
							value={listName}
							dataTestId="product-list-create-name-input"
							onChange={(val) => {
								const text = typeof val === 'string' ? val : val?.target?.value || '';
								setListName(text);
								setErrorName(text.trim() ? '' : 'List Name is required');
							}}
							error={errorName}
						/>
						{errorName && (
							<span className="text-danger font-12" data-testid="product-list-create-name-validation">
								{errorName}
							</span>
						)}
					</div>

					{/* Description Input */}
					<div className="mb-3">
						<InputField
							label="Description"
							placeholder="Enter description"
							type="text"
							required
							name="description"
							multiline
							rows={3}
							value={description}
							dataTestId="product-list-create-description-input"
							onChange={(val) => {
								const text = typeof val === 'string' ? val : val?.target?.value || '';
								setDescription(text);
								setErrorDesc(text.trim() ? '' : 'Description is required');
							}}
							error={errorDesc}
						/>
						{errorDesc && (
							<span className="text-danger font-12" data-testid="product-list-create-description-validation">
								{errorDesc}
							</span>
						)}
					</div>

					<hr className="my-4 text-secondary opacity-25" />

					<div className="mb-3">
						<div className="fw-bold fs-5 text-dark mb-2" data-testid="product-list-create-title">Add Products</div>

						{/* Product Search Bar */}
						<div className="position-relative w-100 mb-3">
							<img
								src={searchIcon}
								alt="search"
								className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary"
								width="16"
								height="16"
							/>
							<input
								type="text"
								className="form-control ps-5 py-2 rounded-3 border-secondary-subtle"
								placeholder="Search by product name and product code"
								value={searchQuery}
								data-testid="product-list-create-product-search"
								onFocus={() => setIsSearchFocused(true)}
								onChange={(e) => {
									setSearchQuery(e.target.value);
									setIsSearchFocused(true);
								}}
							/>
						</div>

						{/* Filtered Products Dropdown List */}
						{(isSearchFocused || searchQuery.trim()) && (
							<div
								className="border border-secondary-subtle rounded-3 p-2 mb-3 bg-white max-h-200 overflow-auto"
								data-testid="product-list-create-product-selector"
								onScroll={handleDropdownScroll}
							>
								{loadingProducts ? (
									<div className="p-3 text-center text-muted font-12">Loading products...</div>
								) : filteredProducts.length > 0 ? (
									<>
										{filteredProducts.map((p) => {
											const pid = p.id || p.product_id;
											const isSelected = selectedProducts.some((item) => (item.id || item.product_id) === pid);
											return (
												<div
													key={pid}
													data-testid={`product-list-create-product-selection-${pid}`}
													className={`d-flex align-items-center justify-content-between p-2 rounded-2 cursor-pointer mb-1 ${
														isSelected ? 'bg-primary-subtle' : 'bg-light'
													}`}
													onMouseDown={(e) => {
														e.preventDefault();
														toggleProduct(p);
													}}
												>
													<div className="d-flex align-items-center gap-2 me-2 flex-grow-1 overflow-hidden">
														<input type="checkbox" className="form-check-input mt-0 cursor-pointer flex-shrink-0" checked={isSelected} readOnly />
														<span className="fw-semibold text-dark font-14 text-break flex-grow-1 overflow-hidden">{p.name}</span>
													</div>
													<span className="text-secondary font-12 flex-shrink-0">{p.code}</span>
												</div>
											);
										})}
										{loadingMoreProducts && (
											<div className="text-center py-2 font-12 text-muted">Loading more...</div>
										)}
									</>
								) : (
									<div className="p-3 text-center text-muted font-12">No products found</div>
								)}
							</div>
						)}

						{/* Products Selection Counter & Clear All */}
						<div className="d-flex justify-content-between align-items-center mb-2 font-14">
							<span className="fw-semibold text-dark" data-testid="product-list-create-selected-count">
								{selectedProducts.length} products selected
							</span>
							{selectedProducts.length > 0 && (
								<span
									className="text-primary cursor-pointer font-12 fw-semibold"
									data-testid="product-list-create-clear-all-selected"
									onClick={() => setSelectedProducts([])}
								>
									Clear All
								</span>
							)}
						</div>

						{/* Selected Product Cards List */}
						{selectedProducts.length > 0 ? (
							<div className="d-flex flex-column gap-2 max-h-200 overflow-auto" data-testid="product-list-create-selected-products-section">
								{selectedProducts.map((p) => {
									const pid = p.id || p.product_id;
									return (
										<div
											key={pid}
											data-testid={`product-list-create-selected-item-${pid}`}
											className="d-flex align-items-center justify-content-between p-2 px-3 bg-light rounded-3 border border-secondary-subtle"
										>
											<div className="d-flex align-items-center gap-3 me-3 flex-grow-1 overflow-hidden">
												<div
													className="d-flex align-items-center justify-content-center rounded-2 text-secondary overflow-hidden flex-shrink-0"
													style={{ width: '36px', height: '36px', backgroundColor: '#e2e8f0' }}
												>
													{p.image ? (
														<img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
													) : (
														<FiImage style={{ fontSize: '18px' }} />
													)}
												</div>
												<div className="flex-grow-1 overflow-hidden">
													<p className="font-14 fw-bold mb-0 text-dark text-break">{p.name}</p>
													<p className="font-12 text-muted mb-0">{p.code}</p>
												</div>
											</div>

											<div className="d-flex align-items-center gap-4 flex-shrink-0">
												<span className="font-12 text-secondary text-nowrap">{p.category || 'General'}</span>
												<FiTrash2
													className="text-danger cursor-pointer flex-shrink-0"
													style={{ fontSize: '18px' }}
													data-testid={`product-list-create-remove-selected-product-${pid}`}
													onClick={() => toggleProduct(p)}
													title="Remove Product"
												/>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<div className="border border-secondary-subtle rounded-3 p-4 text-center bg-white" data-testid="product-list-create-selected-products-section">
								<div className="d-inline-flex align-items-center justify-content-center p-3 bg-primary-subtle text-primary rounded-3 mb-2 fs-4">
									<FiBox />
								</div>
								<div className="fw-bold text-dark fs-6 mb-1">No products selected yet</div>
							</div>
						)}
					</div>

					{/* Modal Footer Buttons */}
					<div className="d-flex justify-content-end gap-3 mt-4 pt-2">
						<Button
							type="submit"
							button="Save"
							className="btn-primary create-list-footer-btn px-4"
							loader={loading}
							onClick={handleSubmit}
							dataTestId="product-list-create-save-button"
						/>
						<Button
							type="button"
							button="Cancel"
							className="btn-gray-cancel create-list-footer-btn px-4"
							onClick={handleClose}
							dataTestId="product-list-create-cancel-button"
						/>
					</div>
				</form>
			</Modal>
		</div>
	);
};

export default CreateNewListModal;
